/* eslint-disable arrow-body-style */
import React, { useState } from 'react'
import styled from 'styled-components'

// Elements
import ParseContent from 'components/shared/ParseContent'
import { ButtonPrimaryCSS } from 'components/elements/Buttons/ButtonPrimary'
import { ButtonSecondaryCSS } from 'components/elements/Buttons/ButtonSecondary'
import FormDuuf, {
  FormDuufGroup,
  FormDuufSubmit,
  useFormDuuf,
} from 'components/related/Form'

// Interface
import { TextProps } from 'components/flex/Text/TextShell'

const Content = styled(ParseContent)`
  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    margin-bottom: 2rem;
    & > strong {
      font-weight: ${({ theme }) => theme.font.weight.extraBold};
    }
  }
`

const StyledFormDuuf = styled(FormDuuf)`
  position: relative;
  background: ${({ theme }) => theme.color.light};

  & .form-duuf-group {
    margin-bottom: 2rem;
  }

  & .form-duuf-textarea,
  & .form-duuf-input {
    width: 100%;
    border-color: #f2f2f2;
    border-style: solid;
    border-width: 1px;
    min-height: 38px;
    padding: 10px 20px;
    background-color: #f2f2f2;
  }

  & .form-duuf-textarea {
    height: 100px;
  }

  & .form-duuf-file-button {
    ${ButtonSecondaryCSS};
  }

  & .form-duuf-file-name {
    margin-left: 0.5rem;
  }

  & .form-duuf-checkbox-label {
    margin-left: 0.5rem;
    margin-bottom: 0;
  }

  & .form-duuf-label-mandatory {
    color: ${({ theme }) => theme.color.secondary};
  }

  & .form-duuf-error {
    color: tomato;
  }

  & .form-duuf-submit {
    ${ButtonPrimaryCSS};

    &-disabled {
      opacity: 0.5;
    }
  }

  & .form-duuf-confirm {
    color: green;
  }

  & .form-duuf-something-wrong {
    color: red;
  }

  & .form-duuf-loading {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    background-color: rgba(255, 255, 255, 0.5);
  }
`

const FormWrapper = styled.div`
  background-color: ${({ theme }) => theme.color.light};
`

const SplitRow = styled.div<{ $wideFormColumn: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  align-items: start;

  @media (min-width: 992px) {
    gap: 100px;
    grid-template-columns: ${({ $wideFormColumn }) =>
      $wideFormColumn
        ? 'minmax(0, 1fr) minmax(0, 1fr)'
        : 'minmax(0, 3fr) minmax(0, 2fr)'};
  }
`

const TextForm: React.FC<TextProps> = ({ fields, location, pageContext }) => {
  const title = location?.pathname.split('/')[2] || 'contact'
  const wideFormColumn =
    pageContext?.breadcrumb?.location.includes('/vacatures/')

  // remove - from title and add spaces and first letter capitalization
  const formattedTitle = title
    .replace(/-/g, ' ')
    .replace(/^\w/, (c: string) => c.toUpperCase())

  return (
    <section className="mb-5 pb-lg-5" id="form">
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-11">
            <SplitRow $wideFormColumn={wideFormColumn}>
              <div className="mb-5 mb-lg-0">
                <Content content={fields.description} />
              </div>
              <div>
                <FormWrapper className="p-lg-5 p-4">
                  <Content content={fields.formtitle} className="mb-5" />
                  <StyledFormDuuf
                    defaultFields={{ input_5: formattedTitle }}
                    id={fields.form || 3}
                    generate={false}
                  >
                    <InnerForm />
                  </StyledFormDuuf>
                </FormWrapper>
              </div>
            </SplitRow>
          </div>
        </div>
      </div>
    </section>
  )
}

function InnerForm() {
  const form = useFormDuuf()

  return (
    <div className="row">
      <FormDuufGroup hideIds={[6]} />
      {form.form.formId === 3 && <CustomUploadField name="input_6" />}
      <FormDuufSubmit />
    </div>
  )
}

function CustomUploadField({ name }: { name: string }) {
  const { formik } = useFormDuuf()
  const [file, setFile] = useState<File | null>(null)
  const inputRef: React.RefObject<HTMLInputElement> = React.useRef(null)
  const MAX_FILE_SIZE = 10 * 1024 * 1024 // 2MB limit (adjust as needed)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="form-duuf-group form-duuf-group-fileupload position-relative">
      <div className="form-duuf-file d-flex align-items-center flex items-center">
        <input
          onChange={(e) => {
            const file = e.target.files![0]

            // Reset error message
            setError(null)

            if (file) {
              // Check if file size exceeds the limit
              if (file.size > MAX_FILE_SIZE) {
                setError('Bestandsgrootte mag niet groter zijn dan 10MB')
                // when click on the same file again, remove the error message
                return // Stop further processing if file size is too large
              }

              setFile(file)
              formik.handleChange({
                target: {
                  // eslint-disable-next-line object-shorthand
                  name: name,
                  value: file,
                },
              })
            }
          }}
          type="file"
          id={name}
          name={name}
          ref={inputRef}
          placeholder="Upload je cv"
          className="form-duuf-file-input d-none"
        />
        <span className="form-duuf-file-button">
          <button
            type="button"
            onClick={() => {
              inputRef?.current?.click()
            }}
          >
            Upload je cv
          </button>
        </span>
        <span className="ms-3 font-size-small">Niet verplicht</span>
        {file && <span className="form-duuf-file-name">{file.name}</span>}
        {error && <span className="form-duuf-error ms-2">{error}</span>}
      </div>
    </div>
  )
}

export default TextForm
